import { useEffect, useRef, useState } from 'react';
import { RefetchFnDynamic, useMutation, useRelayEnvironment } from 'react-relay';
import { SetURLSearchParams, useNavigate } from 'react-router-dom';
import { fetchQuery } from 'relay-runtime';
import { MessageDialog, useDialogManager } from 'src/components/widgets/dialog';
import { showErrorNotification } from 'src/components/widgets/notificationToaster';
import { T, backofficeUrl, toGlobalID, toLocalID } from 'src/shared/helpers';
import { salesChannelsActive$data, salesChannelsActive$key } from '../__generated__/salesChannelsActive.graphql';
import { salesChannelsQuery } from '../__generated__/salesChannelsQuery.graphql';
// Payment step disabled — PaymentSuccessDialog and the payment mutation types are no longer used.
// import PaymentSuccessDialog from './paymentSuccessDialog';
import SwitchSalesChannelDialog from '../switchSalesChannelDialog';
import { salesChannelSwitchQueriesCheckSellerPlanQuery } from './__generated__/salesChannelSwitchQueriesCheckSellerPlanQuery.graphql';
import { salesChannelSwitchQueriesCompletePaymentMethodSetupTaskMutation } from './__generated__/salesChannelSwitchQueriesCompletePaymentMethodSetupTaskMutation.graphql';
// import { salesChannelSwitchQueriesConfirmShopifyChargeMutation } from './__generated__/salesChannelSwitchQueriesConfirmShopifyChargeMutation.graphql';
// import { salesChannelSwitchQueriesCreatePaymentConfirmedTaskMutation } from './__generated__/salesChannelSwitchQueriesCreatePaymentConfirmedTaskMutation.graphql';
import { salesChannelSwitchQueriesCustomPlanOutcomeQuery } from './__generated__/salesChannelSwitchQueriesCustomPlanOutcomeQuery.graphql';
import { salesChannelSwitchQueriesGenerateCreateCustomPlanForSellerTaskMutation } from './__generated__/salesChannelSwitchQueriesGenerateCreateCustomPlanForSellerTaskMutation.graphql';
import { salesChannelSwitchQueriesGenerateDeleteShopifyRecurringChargesTaskMutation } from './__generated__/salesChannelSwitchQueriesGenerateDeleteShopifyRecurringChargesTaskMutation.graphql';
import { salesChannelSwitchQueriesGenerateEditSubscriptionTaskMutation } from './__generated__/salesChannelSwitchQueriesGenerateEditSubscriptionTaskMutation.graphql';
import { salesChannelSwitchQueriesGenerateProductSwitcherTaskMutation } from './__generated__/salesChannelSwitchQueriesGenerateProductSwitcherTaskMutation.graphql';
import { salesChannelSwitchQueriesPollUnsubscribeTaskQuery } from './__generated__/salesChannelSwitchQueriesPollUnsubscribeTaskQuery.graphql';
import { findLastCompletedEditSubscriptionTask, SwitchTaskCreatedDialog } from './completeSwitchDialog';
import CustomPlanFinancialPreviewDialog from './customPlanFinancialPreviewDialog';
import {
    checkSellerPlanQuery,
    completePaymentMethodSetupTaskMutation,
    customPlanOutcomeQuery,
    // Payment step disabled — the charge-confirmation and payment-confirmed-task mutations are no longer used.
    // confirmShopifyChargeMutation,
    // createPaymentConfirmedTaskMutation,
    generateCreateCustomPlanForSellerTaskMutation,
    generateDeleteShopifyRecurringChargesTaskMutation,
    generateEditSubscriptionTaskMutation,
    generateProductSwitcherTaskMutation,
    pollUnsubscribeTaskQuery
} from './salesChannelSwitchQueries';
import { SwitchDialogWindow } from './switchDialogWindow';
import SwitchProgressDialog from './switchProgressDialog';
import { PlanPreparationFailedDialog, SwitchFailedDialog, SwitchTakingTooLongDialog } from './switchFailureDialogs';
import {
    CREATE_CUSTOM_PLAN_FOR_SELLER_TASK_TYPE,
    PAYMENT_METHOD_SETUP_REQUIRED_STORAGE_KEY,
    PaymentMethodSetupStoragePayload,
    SWITCH_DIALOG_IDS,
    SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY,
    SwitchPollingFlowType,
    SwitchSalesChannelIDs,
    SwitchSubscriptionReturnStoragePayload,
    TASKSTATUS,
    UNSUBSCRIBE_TASK_STORAGE_KEY,
    UnsubscribeTaskStoragePayload,
    isTaskPending
} from './switchTypes';
import { getSwitchWizardStepCount, SwitchWizardStep } from './switchWizardSteps';
import { SWITCH_TASKS_TAB_URL } from './switchTaskHelpers';

interface UseSalesChannelSwitchOptions {
    activeSalesChannels: salesChannelsActive$data;
    /** Already defaulted to [] by the page. */
    productSwitcherTasks: NonNullable<salesChannelsActive$data['productSwitcherTasks']>;
    isSellerMigrating: boolean;
    masterEcommerceSettings: salesChannelsActive$data['salesChannels'][number]['ecommerceSettings'];
    /**
     * The seller's current plan, read from the page's query rather than kept in a ref. It is already one render stale
     * by the time a stored dialog element calls back into the flow, and matching that staleness is deliberate.
     */
    planId: number | null | undefined;
    searchParams: URLSearchParams;
    setSearchParams: SetURLSearchParams;
    refetch: RefetchFnDynamic<salesChannelsQuery, salesChannelsActive$key>;
}

interface UseSalesChannelSwitchResult {
    /** Non-null while the seller-plan check for that channel is in flight — drives the row's spinner. */
    checkingSellerPlanForChannelID: string | null;
    handleSwitchSalesChannel: (salesChannelID: string, secondaryEcommerceName: string) => void;
}

/**
 * Owns the whole "promote a secondary sales channel to Master Integration" flow: payment, plan re-approval,
 * task polling, and the resume paths after a Shopify redirect or a page refresh.
 *
 * This is a hook rather than a component on purpose. The polling refs must live in the same component instance as the
 * page so their unmount timing matches today's cleanup effect; a child component would give them a different lifetime.
 *
 * It must also be called from SalesChannels itself, at the position where the switch's first effect sits, never from
 * inside a conditionally-rendered or Suspense-gated child: effect order decides dialog stacking, and a remount would
 * re-run the mount-only restore effect and start a second poll.
 */
export const useSalesChannelSwitch = ({
    activeSalesChannels,
    productSwitcherTasks,
    isSellerMigrating,
    masterEcommerceSettings,
    planId,
    searchParams,
    setSearchParams,
    refetch
}: UseSalesChannelSwitchOptions): UseSalesChannelSwitchResult => {
    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();
    const [checkingSellerPlanForChannelID, setCheckingSellerPlanForChannelID] = useState<string | null>(null);

    // Payment step disabled — the payment-return query params (paymentSuccess/salesChannelID/masterSalesChannelID/charge_id) are no longer read.
    // const paymentSuccess = searchParams.get('paymentSuccess');
    // const salesChannelIDParam = searchParams.get('salesChannelID');
    // const masterSalesChannelIDParam = searchParams.get('masterSalesChannelID');
    // const chargeId = searchParams.get('charge_id');
    // Set by the backend when returning from the Shopify plan re-approval during a sales-channel switch (see handleRevertAndRedirectToShopify)
    const switchSubscriptionApproved = searchParams.get('switchSubscriptionApproved');

    // Payment step disabled — the charge-confirmation and payment-confirmed-task mutations are no longer used.
    // const [confirmShopifyCharge] = useMutation<salesChannelSwitchQueriesConfirmShopifyChargeMutation>(confirmShopifyChargeMutation);
    const [generateUnsubscribeTask] = useMutation<salesChannelSwitchQueriesGenerateEditSubscriptionTaskMutation>(generateEditSubscriptionTaskMutation);
    const [completePaymentMethodSetupTask] =
        useMutation<salesChannelSwitchQueriesCompletePaymentMethodSetupTaskMutation>(completePaymentMethodSetupTaskMutation);
    const [generateProductSwitcherTask] = useMutation<salesChannelSwitchQueriesGenerateProductSwitcherTaskMutation>(generateProductSwitcherTaskMutation);
    const [generateDeleteShopifyRecurringChargesTask] = useMutation<salesChannelSwitchQueriesGenerateDeleteShopifyRecurringChargesTaskMutation>(
        generateDeleteShopifyRecurringChargesTaskMutation
    );
    const [generateCreateCustomPlanForSellerTask] = useMutation<salesChannelSwitchQueriesGenerateCreateCustomPlanForSellerTaskMutation>(
        generateCreateCustomPlanForSellerTaskMutation
    );
    // Payment step disabled — the payment-confirmed task is no longer created.
    // const [createPaymentConfirmedTask] = useMutation<salesChannelSwitchQueriesCreatePaymentConfirmedTaskMutation>(createPaymentConfirmedTaskMutation);

    const Environment = useRelayEnvironment();
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isCompletingPaymentMethodSetupRef = useRef(false);
    const hasStartedProductSwitchRef = useRef(false);
    const dismissedPaymentMethodSetupTaskIDRef = useRef<number | null>(null);
    const hasResumedSwitchFlowRef = useRef(false);

    const getSalesChannelByID = (salesChannelID: string | number | null | undefined) =>
        activeSalesChannels.salesChannels.find(
            (salesChannel) => salesChannel.id === salesChannelID?.toString() || toLocalID(salesChannel.id)?.toString() === salesChannelID?.toString()
        );

    const getSalesChannelName = (salesChannel: ReturnType<typeof getSalesChannelByID>) =>
        salesChannel?.detail?.properties?.name || salesChannel?.ecommerceSettings?.definitiveUrl || salesChannel?.ecommerceSettings?.ecommerceName || '';

    const getWizardStepCount = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs) =>
        getSwitchWizardStepCount(
            getSalesChannelByID(masterSalesChannelID)?.ecommerceSettings ?? masterEcommerceSettings,
            getSalesChannelByID(secondarySalesChannelID)?.ecommerceSettings
        );

    const getTargetEcommerceID = (secondarySalesChannelID: string | number | null | undefined) =>
        getSalesChannelByID(secondarySalesChannelID)?.ecommerceSettings?.ecommerceID;

    const requiresPaymentMethodSetup = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs) =>
        (getSalesChannelByID(masterSalesChannelID)?.ecommerceSettings?.ecommerceName === 'API') !==
        (getSalesChannelByID(secondarySalesChannelID)?.ecommerceSettings?.ecommerceName === 'API');

    const redirectToPaymentMethodSetup = (paymentMethodSetupTaskID: number, targetEcommerceID: number) => {
        localStorage.setItem(PAYMENT_METHOD_SETUP_REQUIRED_STORAGE_KEY, JSON.stringify({ taskID: paymentMethodSetupTaskID }));
        navigate(backofficeUrl(`/Seller/UpgradeTrialSubscription?targetEcommerceID=${targetEcommerceID}`));
    };

    const showPaymentMethodSetupDialog = (paymentMethodSetupTaskID: number, targetEcommerceIDOverride?: number) => {
        const editSubscriptionTask = productSwitcherTasks.find((task) => task.extraData?.paymentMethodSetupTaskID === paymentMethodSetupTaskID);
        const secondarySalesChannelID = editSubscriptionTask?.extraData?.secondarySalesChannelID;
        const targetEcommerceID = targetEcommerceIDOverride ?? getTargetEcommerceID(secondarySalesChannelID);

        if (targetEcommerceID == null) {
            showErrorNotification({
                message: T._('Unable to determine the target ecommerce for the payment method setup. Please refresh the page and try again.', 'Admin')
            });
            return;
        }

        showDialog(
            SWITCH_DIALOG_IDS.paymentMethodSetup,
            <MessageDialog
                windowDecorator={SwitchDialogWindow}
                showCloseButton={true}
                onClose={() => {
                    dismissedPaymentMethodSetupTaskIDRef.current = paymentMethodSetupTaskID;
                    closeDialog(SWITCH_DIALOG_IDS.paymentMethodSetup);
                }}
                title={T._('Payment method required', 'Admin')}
                message={
                    <>
                        {T._(
                            'To complete the Sales Channel switch, you need to enter your payment method again because the two stores use different integrations. Without this step, your account will remain unavailable.',
                            'Admin'
                        )}
                    </>
                }
                buttons={[
                    {
                        label: T._('Set up payment method', 'Admin'),
                        onClick: () => {
                            redirectToPaymentMethodSetup(paymentMethodSetupTaskID, targetEcommerceID);
                        }
                    }
                ]}
            />
        );
    };

    /* Payment step disabled — no Shopify payment redirect happens anymore, so this payment-success return handler is dead.
    // Handle payment success return from Shopify
    useEffect(() => {
        if (paymentSuccess === 'true' && salesChannelIDParam && chargeId) {
            // First, confirm the Shopify charge
            confirmShopifyCharge({
                variables: {
                    input: {
                        shopifyChargeID: chargeId,
                        chargeType: 'SWITCH_SALES_CHANNELS'
                    }
                },
                onCompleted: () => {
                    // Clear query params after successful confirmation
                    setSearchParams({});

                    // Persist payment state to DB and show dialog
                    persistPaymentAndShowDialog(salesChannelIDParam, masterSalesChannelIDParam || '', true, chargeId, true); // checkbox must have been checked to initiate payment
                },
                onError: () => {
                    // Clear query params even on error
                    setSearchParams({});

                    showErrorNotification({
                        message: T._('Failed to confirm payment. Please contact support.', 'Admin')
                    });
                }
            });
        }
    }, [paymentSuccess, salesChannelIDParam, chargeId]);
    */

    // Handle return from Shopify after the seller re-approved the SAME plan (step 3 of the redefined Shopify switch flow).
    // The backend (/Subscription/ActivateShopify) already activated the recurring charge on the new master store and, because
    // handleRevertAndRedirectToShopify passed a returnUrl, redirected back here with switchSubscriptionApproved=true. We read the
    // sales-channel IDs persisted before the redirect and continue with the DeleteShopifyRecurringChargesTask, then the switcher.
    useEffect(() => {
        if (switchSubscriptionApproved !== 'true') return;

        const stored = localStorage.getItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY);
        localStorage.removeItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY);
        setSearchParams({});

        if (!stored) return;
        const { masterSalesChannelID, secondarySalesChannelID } = JSON.parse(stored) as SwitchSubscriptionReturnStoragePayload;
        if (masterSalesChannelID && secondarySalesChannelID) {
            handleDeleteRecurringChargesAndSwitch({ masterSalesChannelID, secondarySalesChannelID });
        }
    }, [switchSubscriptionApproved]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Payment step disabled — no PAYMENT_CONFIRMED task is created anymore, so there is no payment-success dialog to restore.
    // Restore payment success dialog from DB on page load
    useEffect(() => {
        // Skip if URL params will handle it (payment just completed)
        if (paymentSuccess === 'true') return;
        // Skip while the seller is mid-switch beyond the payment step: returning from the Shopify plan approval
        // (switchSubscriptionApproved, handled by the switch-return effect) or a switch sub-flow task is already polling.
        if (switchSubscriptionApproved === 'true') return;
        if (chargeId) return;
        if (localStorage.getItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY)) return;
        if (localStorage.getItem(UNSUBSCRIBE_TASK_STORAGE_KEY)) return;

        const pendingPaymentTask = productSwitcherTasks.find((task) => task.taskType === 'PAYMENT_CONFIRMED' && isTaskPending(task.state));

        if (!pendingPaymentTask) return;

        const { masterSalesChannelID, secondarySalesChannelID } = pendingPaymentTask.extraData;
        if (!masterSalesChannelID || !secondarySalesChannelID) return;

        // Convert local IDs to global IDs for comparison
        const masterGlobalID = toGlobalID(masterSalesChannelID, 'SalesChannel');
        const secondaryGlobalID = toGlobalID(secondarySalesChannelID, 'SalesChannel');

        // Determine if Shopify by checking ecommerce name of the sales channels
        const isShopify = activeSalesChannels.salesChannels.some(
            (sc) => (sc.id === masterGlobalID || sc.id === secondaryGlobalID) && sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
        );

        showPaymentSuccessDialogForSwitch(secondarySalesChannelID.toString(), masterSalesChannelID.toString(), isShopify);
    }, [productSwitcherTasks]);
    */

    // Handle return from subscription page (redirected from dashboard)
    useEffect(() => {
        const subscriptionComplete = searchParams.get('subscriptionComplete');

        if (subscriptionComplete === 'true') {
            const lastEditSubscriptionTask = findLastCompletedEditSubscriptionTask(productSwitcherTasks);
            const storedPaymentMethodSetup = localStorage.getItem(PAYMENT_METHOD_SETUP_REQUIRED_STORAGE_KEY);
            let storedPaymentMethodSetupTaskID: number | null = null;

            try {
                const { taskID } = JSON.parse(storedPaymentMethodSetup ?? '{}') as PaymentMethodSetupStoragePayload;
                if (Number.isSafeInteger(taskID) && taskID > 0) {
                    storedPaymentMethodSetupTaskID = taskID;
                }
            } catch {
                // The payment-method marker is also persisted on EDITSUBSCRIPTION, which is the durable fallback.
            }

            const paymentMethodSetupTaskID = lastEditSubscriptionTask?.extraData?.paymentMethodSetupTaskID ?? storedPaymentMethodSetupTaskID;
            if (paymentMethodSetupTaskID != null) {
                setSearchParams({});
                completePaymentMethodSetupAndStartSwitch(paymentMethodSetupTaskID);
                return;
            }

            const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID || null;
            const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID || null;

            // Only proceed if we have both IDs
            if (masterSalesChannelID && secondarySalesChannelID) {
                // Do not show the dialog if the switch task is already running
                const hasPendingSwitchTask = productSwitcherTasks.some((task) => task.taskType === 'SWITCHSALESCHANNELS' && isTaskPending(task.state));
                if (hasPendingSwitchTask) return;

                // Clear query params
                setSearchParams({});

                handleStartProductSwitch({
                    masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                    secondarySalesChannelID: toGlobalID(secondarySalesChannelID, 'SalesChannel')
                });
            } else {
                // If we can't find the task, clear params and show error
                setSearchParams({});
                showErrorNotification({
                    message: T._('Unable to complete sales channel switch. Please try again or contact support.', 'Admin')
                });
            }
        }
    }, [searchParams, setSearchParams, showDialog, closeDialog, productSwitcherTasks]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resume the switch on page refresh when migration is pending.
    useEffect(() => {
        if (!isSellerMigrating) return;
        if (searchParams.get('subscriptionComplete') === 'true') return;
        if (isCompletingPaymentMethodSetupRef.current) return;
        if (productSwitcherTasks.some((task) => task.taskType === 'SWITCHSALESCHANNELS' && isTaskPending(task.state))) return;

        const lastEditSubscriptionTask = findLastCompletedEditSubscriptionTask(productSwitcherTasks);
        const paymentMethodSetupTaskID = lastEditSubscriptionTask?.extraData?.paymentMethodSetupTaskID;

        if (paymentMethodSetupTaskID != null) {
            if (dismissedPaymentMethodSetupTaskIDRef.current === paymentMethodSetupTaskID) return;

            showPaymentMethodSetupDialog(paymentMethodSetupTaskID);
            return;
        }

        const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID || null;
        const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID || null;

        if (!masterSalesChannelID || !secondarySalesChannelID) return;

        handleStartProductSwitch({
            masterSalesChannelID: toGlobalID(masterSalesChannelID.toString(), 'SalesChannel'),
            secondarySalesChannelID: toGlobalID(secondarySalesChannelID.toString(), 'SalesChannel')
        });
    }, [isSellerMigrating, searchParams, productSwitcherTasks, showDialog, closeDialog]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
        };
    }, []);

    // Restore unsubscribing-plan dialog after a page refresh
    useEffect(() => {
        const stored = localStorage.getItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
        if (!stored) return;

        hasResumedSwitchFlowRef.current = true;

        // salesChannelID is the stored name for the secondary sales channel — see UnsubscribeTaskStoragePayload.
        const {
            taskID,
            salesChannelID: secondarySalesChannelID,
            masterSalesChannelID,
            flowType,
            dialogId
        } = JSON.parse(stored) as UnsubscribeTaskStoragePayload;

        // The resume message and completion callback depend on which stage of the switch was in flight when the page reloaded.
        let resumeMessage = T._('Processing your subscription cancellation...', 'Admin');
        let resumeCallback: (() => void) | undefined;
        if (flowType === 'nonShopify') {
            resumeMessage = T._('Generating sales channel switch task...', 'Admin');
            resumeCallback = () => {
                if (requiresPaymentMethodSetup({ masterSalesChannelID, secondarySalesChannelID })) {
                    refetch({}, { fetchPolicy: 'network-only' });
                    return;
                }

                handleStartProductSwitch({ masterSalesChannelID, secondarySalesChannelID });
            };
        } else if (flowType === 'deleteRecurringCharges') {
            resumeMessage = T._('The sales channel switch task is being created...', 'Admin');
            resumeCallback = () => handleStartProductSwitch({ masterSalesChannelID, secondarySalesChannelID });
        } else if (flowType === 'createCustomPlanShopify' || flowType === 'createCustomPlanNonShopify') {
            // The custom-plan outcome lives on the task itself, so a refresh mid-step resumes by re-reading it rather
            // than by remembering the plan: there is nothing to persist beyond which branch was in flight.
            resumeMessage =
                getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID }) === 2
                    ? T._('Preparing your sales channel switch...', 'Admin')
                    : T._('Preparing your subscription plan...', 'Admin');
            resumeCallback = () =>
                continueAfterCustomPlanTask(taskID, { masterSalesChannelID, secondarySalesChannelID }, flowType === 'createCustomPlanShopify');
        }

        showDialog(
            dialogId ?? SWITCH_DIALOG_IDS.unsubscribingPlan,
            <SwitchProgressDialog
                step={SwitchWizardStep.Subscription}
                stepCount={getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID })}
                showCloseButton={true}
                title={resumeMessage}
                note={T._('You can close this dialog. The process will continue in the background.', 'Admin')}
            />
        );

        startPollingTask({
            taskID,
            masterSalesChannelID,
            secondarySalesChannelID,
            onCompleted: resumeCallback,
            flowType,
            dialogId: dialogId ?? SWITCH_DIALOG_IDS.unsubscribingPlan
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * First step of the subscription stage, run for both branches right after the seller confirms the switch.
     *
     * The task decides by itself whether the seller needs a custom plan for the new (currency, ecommerce) pair: it either
     * creates one, or reports the plan to reuse, or reports neither for combinations it does not handle. Whichever it is
     * decides the plan the rest of the flow works with, so nothing downstream may start before it completes.
     */
    const handleCreateCustomPlanForSeller = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs, isShopify: boolean) => {
        const stepCount = getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID });
        showDialog(
            SWITCH_DIALOG_IDS.creatingCustomPlan,
            <SwitchProgressDialog
                step={SwitchWizardStep.Subscription}
                stepCount={stepCount}
                showCloseButton={false}
                title={stepCount === 2 ? T._('Preparing your sales channel switch...', 'Admin') : T._('Preparing your subscription plan...', 'Admin')}
                note={T._('Please do not close this window.', 'Admin')}
            />
        );

        generateCreateCustomPlanForSellerTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateCreateCustomPlanForSellerTask?.taskID;
                if (taskID == null) {
                    closeDialog(SWITCH_DIALOG_IDS.creatingCustomPlan);
                    showErrorNotification({
                        message: T._('Failed to prepare your subscription plan. Please try again.', 'Admin')
                    });
                    return;
                }
                startPollingTask({
                    taskID: taskID.toString(),
                    masterSalesChannelID,
                    secondarySalesChannelID,
                    onCompleted: () => continueAfterCustomPlanTask(taskID.toString(), { masterSalesChannelID, secondarySalesChannelID }, isShopify),
                    flowType: isShopify ? 'createCustomPlanShopify' : 'createCustomPlanNonShopify',
                    dialogId: SWITCH_DIALOG_IDS.creatingCustomPlan
                });
            },
            onError: () => {
                closeDialog(SWITCH_DIALOG_IDS.creatingCustomPlan);
                showErrorNotification({
                    message: T._('Failed to prepare your subscription plan. Please try again.', 'Admin')
                });
            }
        });
    };

    /**
     * Reads the completed custom-plan task and continues into the branch-specific subscription step with the plan it decided on.
     *
     * The two branches consume the outcome differently on purpose:
     * - Shopify subscribes to an explicit plan on the redirect, so a reused plan is passed just like a created one.
     * - Non-Shopify lets EditSubscription derive the plan when it is reused, and only overrides it with a newly created one.
     */
    const continueAfterCustomPlanTask = (taskID: string, ids: SwitchSalesChannelIDs, isShopify: boolean) => {
        const stepCount = getWizardStepCount(ids);
        // startPollingTask closes the loading dialog the moment the task completes, but reading the outcome below is an
        // async round-trip. Re-assert the loading dialog synchronously so the page never flashes behind an empty overlay
        // while it loads; every branch then swaps it for the next dialog (or navigation) with no visible gap.
        showDialog(
            SWITCH_DIALOG_IDS.creatingCustomPlan,
            <SwitchProgressDialog
                step={SwitchWizardStep.Subscription}
                stepCount={stepCount}
                showCloseButton={false}
                title={stepCount === 2 ? T._('Preparing your sales channel switch...', 'Admin') : T._('Preparing your subscription plan...', 'Admin')}
                note={T._('Please do not close this window.', 'Admin')}
            />
        );

        fetchQuery<salesChannelSwitchQueriesCustomPlanOutcomeQuery>(Environment, customPlanOutcomeQuery, {
            id: toGlobalID(taskID, 'ProductSwitcherTask')
        })
            .toPromise()
            .then((data) => {
                const extraData = data?.productSwitcherTask?.extraData;
                const reusedPlanID = extraData?.reusedPlanID ?? null;
                const createdPlanID = extraData?.customPlan?.planID ?? null;

                const continueWithSubscription = () => {
                    // The subscription handlers below open their own dialog synchronously, so closing this one first keeps the transition seamless.
                    closeDialog(SWITCH_DIALOG_IDS.creatingCustomPlan);
                    if (isShopify) {
                        // Falls back to the seller's current plan when the task handled neither case — today's behaviour.
                        handleRevertAndRedirectToShopify(ids, reusedPlanID ?? createdPlanID);
                    } else {
                        handleEditSubscriptionForNonShopify(ids, reusedPlanID != null ? null : createdPlanID);
                    }
                };
                const financialPreview = extraData?.customPlan?.financialPreview;

                if (!financialPreview) {
                    continueWithSubscription();
                    return;
                }

                closeDialog(SWITCH_DIALOG_IDS.creatingCustomPlan);
                showDialog(
                    SWITCH_DIALOG_IDS.customPlanFinancialPreview,
                    <CustomPlanFinancialPreviewDialog
                        stepCount={stepCount}
                        preview={financialPreview}
                        paymentMethodSetupRequired={
                            (masterEcommerceSettings?.ecommerceName === 'API') !==
                            (activeSalesChannels.salesChannels.find((salesChannel) => salesChannel.id === ids.secondarySalesChannelID)?.ecommerceSettings
                                ?.ecommerceName ===
                                'API')
                        }
                        onContinue={() => {
                            closeDialog(SWITCH_DIALOG_IDS.customPlanFinancialPreview);
                            continueWithSubscription();
                        }}
                    />
                );
            })
            .catch(() => {
                closeDialog(SWITCH_DIALOG_IDS.creatingCustomPlan);
                showErrorNotification({
                    message: T._('Failed to prepare your subscription plan. Please try again.', 'Admin')
                });
            });
    };

    const handleEditSubscriptionForNonShopify = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs, planID: number | null) => {
        const secondaryEcommerceName = activeSalesChannels.salesChannels.find((salesChannel) => salesChannel.id === secondarySalesChannelID)?.ecommerceSettings
            ?.ecommerceName;
        const stepCount = getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID });
        const keepsSubscription = stepCount === 2 && planID == null;

        showDialog(
            SWITCH_DIALOG_IDS.unsubscribingPlan,
            <SwitchProgressDialog
                step={SwitchWizardStep.Subscription}
                stepCount={stepCount}
                showCloseButton={true}
                title={keepsSubscription ? T._('Preparing your sales channel switch...', 'Admin') : T._('Preparing your subscription...', 'Admin')}
                note={
                    keepsSubscription
                        ? T._('Please wait while we prepare your sales channel switch.', 'Admin')
                        : T._('We’re updating your subscription to prepare the sales channel switch.', 'Admin')
                }
                secondaryNote={T._('You can close this dialog. The process will continue in the background.', 'Admin')}
            />
        );

        generateUnsubscribeTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID,
                    environment: 'CUSTOMIZER',
                    // Only set when the custom-plan task created a plan; null leaves the backend deriving it as before.
                    planID: planID
                }
            },
            onCompleted: (response) => {
                if (!response.generateEditSubscriptionTask) {
                    closeDialog(SWITCH_DIALOG_IDS.unsubscribingPlan);
                    showErrorNotification({
                        message: T._('Failed to generate sales channel switch task. Please try again.', 'Admin')
                    });
                    return;
                }
                const taskID = response.generateEditSubscriptionTask.taskID;
                startPollingTask({
                    taskID: taskID.toString(),
                    masterSalesChannelID,
                    secondarySalesChannelID,
                    onCompleted: (paymentMethodSetupTaskID) => {
                        const masterEcommerceName = masterEcommerceSettings?.ecommerceName;

                        // The backend creates paymentMethodSetupTaskID only for API <-> non-API transitions.
                        // In the uninterrupted flow, immediately continue to checkout; a restored flow presents the
                        // recovery dialog instead, so a refresh never silently resumes the external checkout.
                        if ((masterEcommerceName === 'API') !== (secondaryEcommerceName === 'API')) {
                            const targetEcommerceID = getTargetEcommerceID(secondarySalesChannelID);
                            if (paymentMethodSetupTaskID == null || targetEcommerceID == null) {
                                showErrorNotification({
                                    message: T._('Unable to continue with the payment method setup. Please refresh the page and try again.', 'Admin')
                                });
                                return;
                            }

                            if (hasResumedSwitchFlowRef.current) {
                                showPaymentMethodSetupDialog(paymentMethodSetupTaskID, targetEcommerceID);
                                return;
                            }

                            redirectToPaymentMethodSetup(paymentMethodSetupTaskID, targetEcommerceID);
                            return;
                        }

                        handleStartProductSwitch({ masterSalesChannelID, secondarySalesChannelID });
                    },
                    flowType: 'nonShopify'
                });
            },
            onError: () => {
                closeDialog(SWITCH_DIALOG_IDS.unsubscribingPlan);
                showErrorNotification({
                    message: T._('Failed to generate sales channel switch task. Please try again.', 'Admin')
                });
            }
        });
    };

    const startPollingTask = ({
        taskID,
        masterSalesChannelID,
        secondarySalesChannelID,
        onCompleted,
        flowType,
        dialogId = SWITCH_DIALOG_IDS.unsubscribingPlan
    }: SwitchSalesChannelIDs & {
        taskID: string;
        onCompleted?: (paymentMethodSetupTaskID: number | null | undefined) => void;
        flowType?: SwitchPollingFlowType;
        dialogId?: string;
    }) => {
        // Convert taskID to global ID format for the query
        const globalTaskID = toGlobalID(taskID, 'ProductSwitcherTask');

        // Persist task info so the dialog can be restored after a page refresh.
        // Keys are written explicitly: they are the stored wire format read back by the restore effect.
        const storedTask: UnsubscribeTaskStoragePayload = {
            taskID: taskID,
            salesChannelID: secondarySalesChannelID,
            masterSalesChannelID: masterSalesChannelID,
            flowType: flowType,
            dialogId: dialogId
        };
        localStorage.setItem(UNSUBSCRIBE_TASK_STORAGE_KEY, JSON.stringify(storedTask));

        // Poll every 3 seconds
        pollingIntervalRef.current = setInterval(() => {
            fetchQuery<salesChannelSwitchQueriesPollUnsubscribeTaskQuery>(Environment, pollUnsubscribeTaskQuery, { id: globalTaskID })
                .toPromise()
                .then((data) => {
                    if (!data?.productSwitcherTask) return;

                    const task = data.productSwitcherTask;

                    // Check if task is completed (state === 2)
                    if (task.state === TASKSTATUS.Completed) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                        }
                        if (pollingTimeoutRef.current) {
                            clearTimeout(pollingTimeoutRef.current);
                        }
                        localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                        closeDialog(dialogId);
                        if (onCompleted) {
                            onCompleted(task.extraData?.paymentMethodSetupTaskID);
                        } else {
                            window.location.reload();
                        }
                    } else if (task.state === TASKSTATUS.Error) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                        }
                        if (pollingTimeoutRef.current) {
                            clearTimeout(pollingTimeoutRef.current);
                        }
                        localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                        closeDialog(dialogId);

                        // Show error dialog suggesting to contact support
                        showDialog(
                            SWITCH_DIALOG_IDS.unsubscribeTaskError,
                            <SwitchFailedDialog
                                masterSalesChannelName={getSalesChannelName(getSalesChannelByID(masterSalesChannelID))}
                                secondarySalesChannelName={getSalesChannelName(getSalesChannelByID(secondarySalesChannelID))}
                                errorDetail={task.lastError}
                                onClose={() => closeDialog(SWITCH_DIALOG_IDS.unsubscribeTaskError)}
                            />
                        );
                    }
                    // Otherwise continue polling (states 0=Waiting or 1=Processing)
                })
                .catch(() => {
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                    }
                    if (pollingTimeoutRef.current) {
                        clearTimeout(pollingTimeoutRef.current);
                    }
                    localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
                    closeDialog(dialogId);

                    showErrorNotification({
                        message: T._('Failed to check task status. Please refresh the page.', 'Admin')
                    });
                });
        }, 5000); // Poll every 5 seconds

        // Stop polling after 2 hours and recommend contacting support
        pollingTimeoutRef.current = setTimeout(() => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            localStorage.removeItem(UNSUBSCRIBE_TASK_STORAGE_KEY);
            closeDialog(dialogId);
            showDialog(
                SWITCH_DIALOG_IDS.unsubscribeTaskTimeout,
                <SwitchTakingTooLongDialog
                    masterSalesChannelName={getSalesChannelName(getSalesChannelByID(masterSalesChannelID))}
                    secondarySalesChannelName={getSalesChannelName(getSalesChannelByID(secondarySalesChannelID))}
                    onClose={() => closeDialog(SWITCH_DIALOG_IDS.unsubscribeTaskTimeout)}
                />
            );
        }, 7200000); // 2 hours timeout
    };

    const handleStartProductSwitch = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs) => {
        const paymentMethodSetupTaskID = findLastCompletedEditSubscriptionTask(productSwitcherTasks)?.extraData?.paymentMethodSetupTaskID;

        // The backend adds this task ID only for API <-> non-API transitions. Keep the product switch blocked here as well as
        // in the resume flow, so no alternate path can create the migration task before the payment method is configured.
        if (paymentMethodSetupTaskID != null && !isCompletingPaymentMethodSetupRef.current) {
            showPaymentMethodSetupDialog(paymentMethodSetupTaskID);
            return;
        }

        // The subscription-return and page-resume effects can both reach this point in the same render.
        // Keep the lock through success because the page may not receive the new task in its query immediately.
        if (hasStartedProductSwitchRef.current) return;
        hasStartedProductSwitchRef.current = true;

        showDialog(
            SWITCH_DIALOG_IDS.creatingSwitchTask,
            <SwitchProgressDialog
                step={SwitchWizardStep.SwitchTask}
                stepCount={getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID })}
                showCloseButton={true}
                title={T._('The sales channel switch task is being created...', 'Admin')}
                note={T._('You can close this dialog. The process will continue in the background.', 'Admin')}
            />
        );

        // Use generateProductSwitcherTask mutation
        generateProductSwitcherTask({
            variables: {
                input: {
                    masterSalesChannelID: masterSalesChannelID,
                    secondarySalesChannelID: secondarySalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateProductSwitcherTask?.taskID ?? null;
                closeDialog(SWITCH_DIALOG_IDS.creatingSwitchTask);
                // The last wizard step tells the seller the task exists: the CTA opens its details, closing lands on the Tasks tab.
                // Both are full page loads, so no switch dialog survives the navigation and the page shows the new task.
                showDialog(
                    SWITCH_DIALOG_IDS.switchTaskCreated,
                    <SwitchTaskCreatedDialog
                        stepCount={getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID })}
                        taskID={taskID}
                        masterSalesChannelName={getSalesChannelName(getSalesChannelByID(masterSalesChannelID))}
                        secondarySalesChannelName={getSalesChannelName(getSalesChannelByID(secondarySalesChannelID))}
                        onClose={() => {
                            window.location.href = backofficeUrl(SWITCH_TASKS_TAB_URL);
                        }}
                        onGoToTask={() => {
                            window.location.href = backofficeUrl(taskID != null ? `/sales-channels/switch-tasks/${taskID}` : SWITCH_TASKS_TAB_URL);
                        }}
                    />
                );
            },
            onError: () => {
                hasStartedProductSwitchRef.current = false;
                closeDialog(SWITCH_DIALOG_IDS.creatingSwitchTask);
                showErrorNotification({
                    message: T._('Failed to start product switch. Please try again.', 'Admin')
                });
            }
        });
    };

    const completePaymentMethodSetupAndStartSwitch = (paymentMethodSetupTaskID: number) => {
        if (isCompletingPaymentMethodSetupRef.current) return;

        const lastEditSubscriptionTask = findLastCompletedEditSubscriptionTask(productSwitcherTasks);
        const masterSalesChannelID = lastEditSubscriptionTask?.extraData?.masterSalesChannelID;
        const secondarySalesChannelID = lastEditSubscriptionTask?.extraData?.secondarySalesChannelID;
        if (!masterSalesChannelID || !secondarySalesChannelID) {
            showErrorNotification({
                message: T._('Unable to complete sales channel switch. Please try again or contact support.', 'Admin')
            });
            return;
        }

        isCompletingPaymentMethodSetupRef.current = true;
        completePaymentMethodSetupTask({
            variables: { taskID: paymentMethodSetupTaskID },
            onCompleted: () => {
                localStorage.removeItem(PAYMENT_METHOD_SETUP_REQUIRED_STORAGE_KEY);
                handleStartProductSwitch({
                    masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                    secondarySalesChannelID: toGlobalID(secondarySalesChannelID, 'SalesChannel')
                });
            },
            onError: () => {
                isCompletingPaymentMethodSetupRef.current = false;
                showErrorNotification({
                    message: T._('Failed to complete payment method setup. Please try again.', 'Admin')
                });
            }
        });
    };

    // Shopify switch flow (after payment): redirect the seller to Shopify to approve the recurring charge for the SAME plan
    // they already have, then on return delete the old recurring charges and run the product switcher. TASK_TYPE_REVERT_INTEGRATION_CONTEXT
    // is no longer used, so no revert task is fired before the redirect. The plan ID and sales-channel IDs are persisted so the
    // return handler (and a page refresh) can resume the flow.
    const handleRevertAndRedirectToShopify = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs, customPlanID: number | null) => {
        // The plan decided by the custom-plan task wins over the one the page read: it is the one matching the new
        // master's currency and ecommerce. Without it the seller would re-approve a charge in the wrong currency.
        const planIDToSubscribe = customPlanID ?? planId;

        if (planIDToSubscribe == null) {
            showErrorNotification({
                message: T._('Unable to determine your current plan. Please contact support.', 'Admin')
            });
            return;
        }

        showDialog(
            SWITCH_DIALOG_IDS.redirectingToShopify,
            <SwitchProgressDialog
                step={SwitchWizardStep.Subscription}
                stepCount={getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID })}
                showCloseButton={false}
                title={T._('Redirecting you to Shopify to approve your plan...', 'Admin')}
                note={T._('Please do not close this window.', 'Admin')}
            />
        );

        // Keys are written explicitly: they are the stored wire format read back by the switch-return effect.
        const storedReturn: SwitchSubscriptionReturnStoragePayload = {
            masterSalesChannelID: masterSalesChannelID,
            secondarySalesChannelID: secondarySalesChannelID
        };
        localStorage.setItem(SWITCH_SUBSCRIPTION_RETURN_STORAGE_KEY, JSON.stringify(storedReturn));

        // TASK_TYPE_REVERT_INTEGRATION_CONTEXT is no longer used — the revert-integration-context task is skipped.
        // The redirect below (previously the task's onCompleted) now runs directly after persisting the return state.

        const salesChannelID = toLocalID(secondarySalesChannelID) || -1;

        if (salesChannelID === -1) {
            showErrorNotification({
                message: T._('There is an error with the sales channel code. Please contact us at help@zakeke.com', 'Admin')
            });
            return;
        }

        // Reuse the "change plan" redirect (auto-POSTs planID to /Subscription/SubscribeShopify) so the seller approves
        // the same plan on Shopify without the plan-selection page. We pass a same-origin returnUrl so the backend
        // redirects back to /sales-channels (instead of /Dashboard?planChanged=true) once the charge is activated.
        // salesChannelID is the secondary (new master-to-be) sales channel — the endpoint activates the new recurring charge on it.
        const returnUrl = `${window.location.origin}${backofficeUrl('/sales-channels')}?switchSubscriptionApproved=true`;
        window.location.href = backofficeUrl(
            `/seller/redirectForUpgradePlanForShopify?planID=${planIDToSubscribe}&salesChannelID=${encodeURIComponent(salesChannelID)}&returnUrl=${encodeURIComponent(returnUrl)}`
        );
    };

    // Step 4: after the seller re-approves the plan on Shopify and the charge is verified, delete the Shopify recurring charges
    // of the secondary sales channel (the one being promoted to master), then run the product switcher and redirect to the switch-tasks page.
    const handleDeleteRecurringChargesAndSwitch = ({ masterSalesChannelID, secondarySalesChannelID }: SwitchSalesChannelIDs) => {
        showDialog(
            SWITCH_DIALOG_IDS.creatingSwitchTask,
            <SwitchProgressDialog
                step={SwitchWizardStep.SwitchTask}
                stepCount={getWizardStepCount({ masterSalesChannelID, secondarySalesChannelID })}
                showCloseButton={true}
                title={T._('The sales channel switch task is being created...', 'Admin')}
                note={T._('You can close this dialog. The process will continue in the background.', 'Admin')}
            />
        );

        generateDeleteShopifyRecurringChargesTask({
            variables: {
                input: {
                    salesChannelID: masterSalesChannelID
                }
            },
            onCompleted: (response) => {
                const taskID = response.generateDeleteShopifyRecurringChargesTask?.taskID;
                if (!taskID) {
                    closeDialog(SWITCH_DIALOG_IDS.creatingSwitchTask);
                    showErrorNotification({
                        message: T._('Failed to create the sales channel switch task. Please try again.', 'Admin')
                    });
                    return;
                }
                startPollingTask({
                    taskID: taskID.toString(),
                    masterSalesChannelID,
                    secondarySalesChannelID,
                    onCompleted: () => handleStartProductSwitch({ masterSalesChannelID, secondarySalesChannelID }),
                    flowType: 'deleteRecurringCharges',
                    dialogId: SWITCH_DIALOG_IDS.creatingSwitchTask
                });
            },
            onError: () => {
                closeDialog(SWITCH_DIALOG_IDS.creatingSwitchTask);
                showErrorNotification({
                    message: T._('Failed to create the sales channel switch task. Please try again.', 'Admin')
                });
            }
        });
    };

    // Payment step disabled — proceed straight from the Review dialog to the next step (Subscription) for both cases.
    // This carries over the branch that used to live in showPaymentSuccessDialogForSwitch's onProceedToNextStep,
    // minus the PaymentSuccessDialog and the persisted PAYMENT_CONFIRMED task.
    const proceedToSubscriptionStep = (salesChannelID: string, masterSalesChannelID: string, isShopify: boolean) => {
        // Both branches start with the custom-plan task: it decides the plan the Shopify redirect subscribes to and the
        // one EditSubscriptionTask is given, so it must run before either of them. See continueAfterCustomPlanTask.
        handleCreateCustomPlanForSeller(
            {
                masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                secondarySalesChannelID: toGlobalID(salesChannelID, 'SalesChannel')
            },
            isShopify
        );
    };

    /* Payment step disabled — the payment-success dialog and the persisted PAYMENT_CONFIRMED task are no longer used.
    const showPaymentSuccessDialogForSwitch = (salesChannelID: string, masterSalesChannelID: string, isShopify: boolean) => {
        showDialog(
            SWITCH_DIALOG_IDS.paymentSuccess,
            <PaymentSuccessDialog
                isShopify={isShopify}
                onProceedToNextStep={() => {
                    closeDialog(SWITCH_DIALOG_IDS.paymentSuccess);
                    if (isShopify) {
                        // Shopify: re-approve the SAME plan on Shopify → delete old recurring charges → product switch.
                        // TASK_TYPE_SWITCH_INTEGRATION_CONTEXT is no longer used, so we skip straight to the plan re-approval redirect.
                        handleRevertAndRedirectToShopify({
                            masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                            secondarySalesChannelID: toGlobalID(salesChannelID, 'SalesChannel')
                        });
                    } else {
                        // Non-Shopify: generate EditSubscriptionTask first, then ProductSwitcherTask on completion
                        handleEditSubscriptionForNonShopify({
                            masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                            secondarySalesChannelID: toGlobalID(salesChannelID, 'SalesChannel')
                        });
                    }
                }}
                onCancel={() => {
                    closeDialog(SWITCH_DIALOG_IDS.paymentSuccess);
                }}
            />
        );
    };

    const persistPaymentAndShowDialog = (
        salesChannelID: string,
        masterSalesChannelID: string,
        isShopify: boolean,
        shopifyChargeID: string | null,
        hasAcknowledgedInfo: boolean
    ) => {
        createPaymentConfirmedTask({
            variables: {
                input: {
                    masterSalesChannelID: toGlobalID(masterSalesChannelID, 'SalesChannel'),
                    secondarySalesChannelID: toGlobalID(salesChannelID, 'SalesChannel'),
                    shopifyChargeID: shopifyChargeID,
                    hasAcknowledgedInformation: hasAcknowledgedInfo
                }
            },
            onCompleted: () => {
                refetch({}, { fetchPolicy: 'network-only' });
            }
        });

        showPaymentSuccessDialogForSwitch(salesChannelID, masterSalesChannelID, isShopify);
    };
    */

    const openSwitchSalesChannelDialog = (
        salesChannelID: string,
        secondaryEcommerceName: string,
        configuratorModelsCount?: number,
        hasPodProducts?: boolean
    ) => {
        const secondarySalesChannel = activeSalesChannels.salesChannels.find((salesChannel) => salesChannel.id === salesChannelID);
        const masterSalesChannel = activeSalesChannels.salesChannels.find((salesChannel) => toLocalID(salesChannel.id) === 1);
        const secondaryEcommerceSettings = secondarySalesChannel?.ecommerceSettings;

        showDialog(
            SWITCH_DIALOG_IDS.switchSalesChannel,
            <SwitchSalesChannelDialog
                secondarySalesChannelID={salesChannelID}
                secondaryEcommerceName={secondaryEcommerceName}
                secondarySalesChannelName={
                    secondarySalesChannel?.detail?.properties?.name || secondaryEcommerceSettings?.definitiveUrl || secondaryEcommerceName
                }
                secondaryEcommerceID={secondaryEcommerceSettings?.ecommerceID}
                secondaryCurrencyID={secondaryEcommerceSettings?.currencyID}
                secondaryCurrencyCode={secondaryEcommerceSettings?.currencyCode}
                masterSalesChannelID={masterEcommerceSettings?.id || ''}
                masterEcommerceName={masterEcommerceSettings?.ecommerceName || 'NONE'}
                masterSalesChannelName={
                    masterSalesChannel?.detail?.properties?.name || masterEcommerceSettings?.definitiveUrl || masterEcommerceSettings?.ecommerceName || 'NONE'
                }
                masterEcommerceID={masterEcommerceSettings?.ecommerceID}
                masterCurrencyID={masterEcommerceSettings?.currencyID}
                masterCurrencyCode={masterEcommerceSettings?.currencyCode}
                configuratorModelsCount={configuratorModelsCount}
                hasPodProducts={hasPodProducts}
                onClosed={() => {
                    // Just close the dialog — merely opening/closing the review changes nothing, so no refetch (which reloaded the whole page).
                    closeDialog(SWITCH_DIALOG_IDS.switchSalesChannel);
                }}
                onPaymentSuccess={(scID, masterScID, isShopify) => {
                    // Payment step disabled — skip persisting the PAYMENT_CONFIRMED task and the PaymentSuccessDialog; proceed straight to the next step.
                    proceedToSubscriptionStep(scID, masterScID, isShopify);
                }}
            />
        );
    };

    const handleSwitchSalesChannel = (salesChannelID: string, secondaryEcommerceName: string) => {
        const paymentMethodSetupTaskID = isSellerMigrating
            ? findLastCompletedEditSubscriptionTask(productSwitcherTasks)?.extraData?.paymentMethodSetupTaskID
            : null;

        if (paymentMethodSetupTaskID != null) {
            dismissedPaymentMethodSetupTaskIDRef.current = null;
            showPaymentMethodSetupDialog(paymentMethodSetupTaskID);
            return;
        }

        const failedCustomPlanTask = productSwitcherTasks.find(
            (task) => task.taskType === CREATE_CUSTOM_PLAN_FOR_SELLER_TASK_TYPE && task.state === TASKSTATUS.Error
        );

        if (failedCustomPlanTask) {
            showDialog(
                SWITCH_DIALOG_IDS.customPlanTaskError,
                <PlanPreparationFailedDialog
                    masterSalesChannelName={getSalesChannelName(activeSalesChannels.salesChannels.find((salesChannel) => toLocalID(salesChannel.id) === 1))}
                    secondarySalesChannelName={getSalesChannelName(getSalesChannelByID(salesChannelID))}
                    errorDetail={failedCustomPlanTask.lastError}
                    onClose={() => closeDialog(SWITCH_DIALOG_IDS.customPlanTaskError)}
                />
            );
            return;
        }

        // If the secondary store is not Shopify/API and the master integration is API, the switch
        // requires manual intervention — prompt the user to contact support.
        // if (secondaryEcommerceName !== 'SHOPIFY' && secondaryEcommerceName !== 'API' && masterEcommerceSettings?.ecommerceName === 'API') {
        //     showDialog(
        //         SWITCH_DIALOG_IDS.switchNotSupported,
        //         <MessageDialog
        //             message={
        //                 <>
        //                     <h3>{T._('Switch not supported', 'Admin')}</h3>
        //                     <p>
        //                         {T._(
        //                             'Switching this store to Master Integration is not supported when the current Master Integration is API. Please contact Zakeke support for assistance.',
        //                             'Admin'
        //                         )}
        //                     </p>
        //                 </>
        //             }
        //             buttons={[{ label: T._('OK', 'Admin'), onClick: () => closeDialog(SWITCH_DIALOG_IDS.switchNotSupported) }]}
        //         />
        //     );
        //     return;
        // }

        /* Payment step disabled — there is no PAYMENT_CONFIRMED task to reopen, so this guard is dead.
        // If a PAYMENT_CONFIRMED task is already waiting for this channel, the user paid
        // but closed the success dialog — reopen it instead of restarting the payment flow.
        const localID = toLocalID(salesChannelID)?.toString();
        const pendingPaymentTask = productSwitcherTasks.find(
            (task) => task.taskType === 'PAYMENT_CONFIRMED' && isTaskPending(task.state) && task.extraData.secondarySalesChannelID?.toString() === localID
        );

        if (pendingPaymentTask) {
            const { masterSalesChannelID, secondarySalesChannelID } = pendingPaymentTask.extraData;
            if (masterSalesChannelID && secondarySalesChannelID) {
                const masterGlobalID = toGlobalID(masterSalesChannelID.toString(), 'SalesChannel');
                const secondaryGlobalID = toGlobalID(secondarySalesChannelID.toString(), 'SalesChannel');
                const isShopify = activeSalesChannels.salesChannels.some(
                    (sc) => (sc.id === masterGlobalID || sc.id === secondaryGlobalID) && sc.ecommerceSettings?.ecommerceName === 'SHOPIFY'
                );
                showPaymentSuccessDialogForSwitch(secondarySalesChannelID.toString(), masterSalesChannelID.toString(), isShopify);
                return;
            }
        }
        */

        const proceedWithSellerPlanCheck = () => {
            fetchQuery<salesChannelSwitchQueriesCheckSellerPlanQuery>(Environment, checkSellerPlanQuery, {})
                .toPromise()
                .then((data) => {
                    setCheckingSellerPlanForChannelID(null);
                    // The seller-plan preflight is intentionally disabled so V1 plans and sellers with unpublished products can proceed with the switch.
                    openSwitchSalesChannelDialog(salesChannelID, secondaryEcommerceName, data?.modelsCount ?? undefined, data?.hasPodProducts ?? undefined);
                })
                .catch(() => {
                    setCheckingSellerPlanForChannelID(null);
                });
        };

        setCheckingSellerPlanForChannelID(salesChannelID);

        proceedWithSellerPlanCheck();
    };

    return { checkingSellerPlanForChannelID, handleSwitchSalesChannel };
};

export default useSalesChannelSwitch;
